import { Box } from "@material-ui/core";
import clsx from "clsx";
import React from "react";
import { RouteComponentProps } from "react-router-dom";

import {
  B2BSpinner,
  BackButton,
  getTravelCreditCardContent,
  getTravelCreditHistoryModalProps,
  getTravelOfferCardContent,
  IconName,
  ITravelOfferCardContent,
  LoadingIndicator,
  MobileFloatingButton,
  MobilePopoverCard,
  TravelWalletSingleSelectionStep,
  TravelWalletSingleSelectionWorkflow,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
} from "halifax";
import {
  CallState,
  SELECTED_TRAVEL_OFFER,
  SelectedTravelOfferScreen,
  TravelWalletCredit,
  TravelWalletOffer,
  VIEWED_OFFER_FAQ,
} from "redmond";

import { config } from "../../../../api/config";
import { trackEvent } from "../../../../api/v0/analytics/trackEvent";
import { ClientContext } from "../../../../App";
import { getConfigTenant, isCorpTenant, hasIneligibleTravelOffer } from "@capone/common";
import {
  AVAILABLE,
  CONTROL,
  CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_SALE,
  TRAVEL_SALE_VARIANTS,
  TRAVEL_WALLET_CREDITS_EXPERIMENT,
  useExperiments,
} from "../../../../context/experiments";
import { TravelOfferSelectionConnectorProps } from "./container";
import {
  CONTINUE,
  CREDITS_CTA,
  getOffersModalTitle,
  getSelectedCreditDescription,
  getSelectedOfferDescription,
  LOADING_OFFERS,
  LOADING_WALLET,
  MODAL_HOTELS_HEADER_TEXT_CREDITS,
  VIEW_OFFER_DETAILS_CTA_COPY,
} from "./textConstants";

import "./styles.scss";

const DO_NOT_APPLY_REWARDS_KEY = "do-not-apply-rewards";

export interface ITravelOfferSelectionProps
  extends TravelOfferSelectionConnectorProps,
    RouteComponentProps {
  className?: string;
  isMobile?: boolean;
  progressStep?: TravelWalletSingleSelectionStep;
  mobileHeaderElement?: JSX.Element;
  onGoBack?: () => void;
  onContinueClick?: () => void;
}

export const TravelOfferSelection = (props: ITravelOfferSelectionProps) => {
  const {
    className,
    offers,
    bestOverallOffer,
    offerToApply,
    setOfferToApply,
    isMobile,
    progressStep,
    mobileHeaderElement,
    onGoBack,
    onContinueClick,
    fetchApplicableTravelWalletItemsCallState,
    selectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    setSelectedRewardsPaymentTotal,
    resetPaymentCardSelectedAccounts,
    credit,
    creditBreakdown,
    creditToApply,
    setCreditToApply,
    maxApplicableTravelWalletCreditOnlyAmount,
    isTravelWalletPaymentOnly,
    creditsTransactionHistory,
    allWalletOffers,
    bookingTotal = 0,
    currentFunnel,
  } = props;
  const [travelOfferCardContent, setTravelOfferCardContent] = React.useState<
    ITravelOfferCardContent | undefined
  >(undefined);
  const [currentSelectedOffer, setCurrentSelectedOffer] = React.useState<
    TravelWalletOffer | undefined
  >(undefined);
  const [currentSelectedCredit, setCurrentSelectedCredit] = React.useState<
    TravelWalletCredit | undefined
  >(undefined);
  const [openModal, setOpenModal] = React.useState(false);

  const expState = useExperiments();
  const travelWalletCreditsExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_WALLET_CREDITS_EXPERIMENT
  );
  const isTravelWalletCreditsExperiment = React.useMemo(
    () => travelWalletCreditsExperiment === AVAILABLE,
    [travelWalletCreditsExperiment]
  );

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT
  );
  const isTravelCreditHistoryExperiment = React.useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment]
  );

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS
  );
  const isTravelSaleEnabled = travelSaleVariant !== CONTROL;

  const firstName =
    React.useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const isLoading =
    fetchApplicableTravelWalletItemsCallState === CallState.InProcess;

  const filteredOffers = React.useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers]
  );

  const filteredBestOverallOffer = React.useMemo(
    () =>
      bestOverallOffer && isSpendAndGetOffer(bestOverallOffer.descriptions)
        ? undefined
        : bestOverallOffer,
    [bestOverallOffer]
  );

  const isFtbEnabled =
    getExperimentVariant(
      expState.experiments,
      CORP_FTB_OFFER_ADDITIONAL_PLACEMENTS
    ) === AVAILABLE;

  const hasIneligibleFTB = React.useMemo(
    () =>
      isFtbEnabled &&
      isCorpTenant(getConfigTenant()) &&
      hasIneligibleTravelOffer(allWalletOffers, filteredOffers, currentFunnel),
    [isFtbEnabled, filteredOffers, allWalletOffers, currentFunnel]
  );

  const trackViewedOfferFaq = () => {
    trackEvent({
      eventName: VIEWED_OFFER_FAQ,
      properties: {
        screen: SelectedTravelOfferScreen.HOTEL_CHECKOUT,
      },
    });
  };

  React.useEffect(() => {
    switch (progressStep) {
      case TravelWalletSingleSelectionStep.Main:
        setOpenModal(false);
        break;
      case TravelWalletSingleSelectionStep.TravelWalletSelection:
        setOpenModal(true);
        break;
      default:
        break;
    }
  }, [progressStep]);

  React.useEffect(() => {
    if (
      (!isTravelWalletCreditsExperiment && filteredBestOverallOffer) ||
      Math.abs(filteredBestOverallOffer?.amount.amount || 0) >
        Math.abs(credit?.amount.amount || 0)
    ) {
      setOfferToApply(filteredBestOverallOffer);
      setCurrentSelectedOffer(filteredBestOverallOffer);
      setCreditToApply(undefined);
      setCurrentSelectedCredit(undefined);
    } else if (credit) {
      setCreditToApply(credit);
      setCurrentSelectedCredit(credit);
      setOfferToApply(undefined);
      setCurrentSelectedOffer(undefined);
    }
  }, [filteredBestOverallOffer, credit]);

  React.useEffect(() => {
    if (currentSelectedOffer) {
      const content = getTravelOfferCardContent(currentSelectedOffer);
      setTravelOfferCardContent(content);
    }
  }, [currentSelectedOffer]);

  React.useEffect(() => {
    const hasRealRewardsSelection =
      selectedRewardsPaymentAccountId &&
      selectedRewardsPaymentAccountId !== DO_NOT_APPLY_REWARDS_KEY;

    if (selectedPaymentMethodId || hasRealRewardsSelection) {
      resetPaymentCardSelectedAccounts();
      setSelectedRewardsPaymentTotal("", null, null, null);
    }
  }, [offerToApply, creditToApply]);

  const creditDetailsCardProps = React.useMemo(
    () =>
      getTravelCreditCardContent(
        creditBreakdown,
        config.TENANT,
        firstName,
        credit?.amount
      ),
    [firstName, credit, creditBreakdown]
  );

  if ((!filteredOffers || !filteredBestOverallOffer) && !credit && !isLoading && !hasIneligibleFTB) return null;

  return isMobile ? (
    <MobilePopoverCard
      className={clsx("mobile-select-offers-popup", "b2b")}
      open={openModal}
      onClose={() => setOpenModal(false)}
      fullScreen
      headerElement={mobileHeaderElement}
      topLeftButton={
        onGoBack && (
          <BackButton
            className="hotel-offers-popup-back-button"
            onClick={onGoBack}
          />
        )
      }
    >
      <TravelWalletSingleSelectionWorkflow
        travelOfferCardContent={travelOfferCardContent}
        modalCTACopy={
          currentSelectedCredit ? CREDITS_CTA : VIEW_OFFER_DETAILS_CTA_COPY
        }
        modalHeaderText={
          currentSelectedCredit
            ? MODAL_HOTELS_HEADER_TEXT_CREDITS(firstName)
            : undefined
        }
        offers={filteredOffers || []}
        className={className}
        offerToApply={offerToApply}
        setOfferToApply={setOfferToApply}
        currentSelectedOffer={currentSelectedOffer}
        setCurrentSelectedOffer={setCurrentSelectedOffer}
        getOfferDescription={getSelectedOfferDescription}
        modalTitle={getOffersModalTitle(filteredOffers || [], true)}
        isMobile={isMobile}
        onSetOpenModal={(open) => {
          if (open && offerToApply) {
            trackEvent({
              eventName: SELECTED_TRAVEL_OFFER,
              properties: {
                screen: SelectedTravelOfferScreen.HOTEL_CHECKOUT,
                offer_count: filteredOffers?.length,
                ...offerToApply?.trackingPropertiesV2?.properties,
              },
              encryptedProperties: [
                offerToApply?.trackingPropertiesV2?.encryptedProperties ?? "",
              ],
            });
          }
        }}
        onFirstTravelOfferCardChange={trackViewedOfferFaq}
        credit={isTravelWalletCreditsExperiment ? credit : undefined}
        creditToApply={creditToApply}
        setCreditToApply={setCreditToApply}
        currentSelectedCredit={currentSelectedCredit}
        setCurrentSelectedCredit={setCurrentSelectedCredit}
        getCreditDescription={getSelectedCreditDescription}
        creditAmountToApply={maxApplicableTravelWalletCreditOnlyAmount}
        creditDetailsCardProps={{ cardContent: creditDetailsCardProps }}
        isTravelWalletPaymentOnly={isTravelWalletPaymentOnly}
        showCreditHistory={
          isTravelCreditHistoryExperiment && !!creditsTransactionHistory?.length
        }
        travelCreditHistoryProps={
          isTravelCreditHistoryExperiment && !!creditsTransactionHistory?.length
            ? getTravelCreditHistoryModalProps(
                firstName,
                creditsTransactionHistory,
                credit
              )
            : undefined
        }
        isTravelSale={isTravelSaleEnabled}
        allWalletOffers={allWalletOffers}
        bookingTotal={bookingTotal}
        currentFunnel={currentFunnel}
      />
      {onContinueClick && (
        <MobileFloatingButton
          className="offers-continue-button"
          onClick={onContinueClick}
          wrapperClassName="b2b"
        >
          {CONTINUE}
        </MobileFloatingButton>
      )}
    </MobilePopoverCard>
  ) : (
    <Box className="checkout-travel-offer-container">
      {isLoading ? (
        <LoadingIndicator
          indicator={B2BSpinner}
          message={
            isTravelWalletCreditsExperiment ? LOADING_WALLET : LOADING_OFFERS
          }
          indicatorSize="medium"
          textAlign="center"
        />
      ) : (
        <TravelWalletSingleSelectionWorkflow
          travelOfferCardContent={travelOfferCardContent}
          modalCTACopy={
            currentSelectedCredit ? CREDITS_CTA : VIEW_OFFER_DETAILS_CTA_COPY
          }
          modalHeaderText={
            currentSelectedCredit
              ? MODAL_HOTELS_HEADER_TEXT_CREDITS(firstName)
              : undefined
          }
          offers={filteredOffers || []}
          className={className}
          offerToApply={offerToApply}
          setOfferToApply={setOfferToApply}
          currentSelectedOffer={currentSelectedOffer}
          setCurrentSelectedOffer={setCurrentSelectedOffer}
          getOfferDescription={getSelectedOfferDescription}
          modalTitle={getOffersModalTitle(filteredOffers || [], false)}
          isMobile={isMobile}
          onSetOpenModal={(open) => {
            if (open && offerToApply) {
              trackEvent({
                eventName: SELECTED_TRAVEL_OFFER,
                properties: {
                  screen: SelectedTravelOfferScreen.HOTEL_CHECKOUT,
                  offer_count: filteredOffers?.length,
                  ...offerToApply?.trackingPropertiesV2?.properties,
                },
                encryptedProperties: [
                  offerToApply?.trackingPropertiesV2?.encryptedProperties ?? "",
                ],
              });
            }
          }}
          onFirstTravelOfferCardChange={trackViewedOfferFaq}
          credit={isTravelWalletCreditsExperiment ? credit : undefined}
          creditToApply={creditToApply}
          setCreditToApply={setCreditToApply}
          currentSelectedCredit={currentSelectedCredit}
          setCurrentSelectedCredit={setCurrentSelectedCredit}
          getCreditDescription={getSelectedCreditDescription}
          creditAmountToApply={maxApplicableTravelWalletCreditOnlyAmount}
          creditDetailsCardProps={{ cardContent: creditDetailsCardProps }}
          isTravelWalletPaymentOnly={isTravelWalletPaymentOnly}
          showCreditHistory={
            isTravelCreditHistoryExperiment &&
            !!creditsTransactionHistory?.length
          }
          travelCreditHistoryProps={
            isTravelCreditHistoryExperiment &&
            !!creditsTransactionHistory?.length
              ? getTravelCreditHistoryModalProps(
                  firstName,
                  creditsTransactionHistory,
                  credit,
                  IconName.PiggyBankCircleOutline
                )
              : undefined
          }
          isTravelSale={isTravelSaleEnabled}
          allWalletOffers={allWalletOffers}
          bookingTotal={bookingTotal}
          currentFunnel={currentFunnel}
        />
      )}
    </Box>
  );
};
